/**
 * Cross-component selection + filter store.
 *
 * One source of truth for "what's selected", "what's hovered", and the
 * filter panel state. Constellation reads it; MemoryDetail reads it; the
 * top toolbar reads/writes the filter knobs. Everything is reactive via
 * runes — components just bind to fields.
 */

import { SvelteSet, SvelteMap } from 'svelte/reactivity'
import type { LayoutPoint } from './api.svelte'
import { mco } from './mcoAuth.svelte'

const NR_BASE =
  (import.meta as unknown as { env: Record<string, string | undefined> }).env
    .VITE_NR_BASE ?? 'https://red.psyopsys.com'

export type ProjectionAlgo = 'pca' | 'umap' | 'tsne' | 'force'
export type ImportanceTier = 'all' | 'baseline' | 'high' | 'elite' | 'apex'
export type ViewMode = '2d' | '3d'

/**
 * Edge rendering mode (mirrors original Mem-Dash constellation):
 *   none     — no edges
 *   selected — only edges connected to the currently selected memory
 *   elite    — edges between memories where both endpoints have importance ≥ 15
 *   apex     — edges between memories where both endpoints have importance ≥ 20
 *   all      — every edge among visible points (noisy at scale; use with low limit)
 */
export type EdgeMode = 'none' | 'selected' | 'elite' | 'apex' | 'all'

interface SelectionState {
  hoveredId: number | null
  hoveredPoint: LayoutPoint | null
  /** id of the currently selected memory; null when nothing selected. */
  selectedId: number | null
}

interface FilterState {
  projection: ProjectionAlgo
  limit: number
  tier: ImportanceTier
  edgeMode: EdgeMode
  edgeStrengthMin: number
  viewMode: ViewMode
  /** Single-text search by topic substring (client-side filter). */
  search: string
  /** When true, multiplies Z by 1.8x in 3D mode for better depth perception. */
  zAmplify: boolean

  /** Independent camera toggles. Both can be on simultaneously: orbit
   * rotates the camera around the current selection while walk advances
   * the selection (and thus the orbit center) every walkIntervalMs. */
  orbit: boolean
  walk: boolean

  /** Orbit settings. */
  orbitSpeed: number       /* deg/frame, 0.1-2.5 */
  orbitDirection: 1 | -1   /* +1 = clockwise, -1 = counter-clockwise */
  orbitPitch: number       /* rotationX in degrees, 0-75 */
  orbitAutoTilt: boolean   /* slowly oscillate pitch for cinematic feel */

  /** Auto-walk advance interval in ms when walk=true. */
  walkIntervalMs: number
}

export const selection = $state<SelectionState>({
  hoveredId: null,
  hoveredPoint: null,
  selectedId: null,
})

export const filters = $state<FilterState>({
  projection: 'pca',
  limit: 5000,
  tier: 'all',
  edgeMode: 'selected',  /* sane default — show edges from clicked point */
  edgeStrengthMin: 0.3,
  viewMode: '2d',
  search: '',
  zAmplify: true,
  orbit: false,
  walk: false,
  orbitSpeed: 0.4,
  orbitDirection: 1,
  orbitPitch: 30,
  orbitAutoTilt: false,
  walkIntervalMs: 3000,
})

/**
 * Write-mode lock. Independent from apiStatus.writesEnabled — the API can
 * support writes while the UI is still locked. User has to deliberately
 * unlock writes per session (acts as an "armed" state for destructive ops).
 *
 * The toolbar toggle flips `unlocked`. Write actions check both:
 *   - apiStatus.writesEnabled (server-side)
 *   - writeMode.unlocked (client-side intent)
 */
export const writeMode = $state<{
  unlocked: boolean
  /** Show the AddMemoryDialog when true. */
  addDialogOpen: boolean
  /** Show the IngestDocumentDialog when true. */
  ingestDialogOpen: boolean
}>({
  unlocked: false,
  addDialogOpen: false,
  ingestDialogOpen: false,
})

/**
 * Ingest-queue snapshot. Fetched from DC's GET /ingest/jobs (the async
 * queue endpoint, milestone 91420). The Drawer's writeMode panel renders
 * this list as DOCUMENTS with per-row status pills + chunk progress.
 *
 * Tenant-scoped: server filters by the bearer's primaryTenantId, so each
 * user sees only their own queue.
 */
export interface IngestJob {
  job_id: string
  document_id: string
  topic: string
  filename: string | null
  mime_type: string
  size_bytes: number
  status: 'queued' | 'running' | 'done' | 'failed'
  total_chunks: number | null
  processed_chunks: number
  error: string | null
  created_by_subject: string | null
  created_at: string
  started_at: string | null
  finished_at: string | null
  /** First (parent-less) chunk's claude_memory.id. NULL for queued/running
   *  jobs that haven't produced chunks yet OR if the chunks were deleted.
   *  UI uses it for click-to-jump → selection.selectedId. */
  head_memory_id: number | null
}

export const ingestQueue = $state<{
  jobs: IngestJob[]
  loading: boolean
  lastFetchedAt: number | null
  error: string | null
  /** Job ids with an in-flight rename / delete — UI disables buttons. */
  mutating: SvelteSet<string>
}>({
  jobs: [],
  loading: false,
  lastFetchedAt: null,
  error: null,
  mutating: new SvelteSet<string>(),
})

export function ingestQueueHasActive(): boolean {
  return ingestQueue.jobs.some((j) => j.status === 'queued' || j.status === 'running')
}

/**
 * Files-screen sub-tab state. Today UPLOADS is the only sub-tab; the type
 * is open to future ATTACHMENTS/MEDIA/etc additions.
 */
export const filesPanel = $state<{ tab: 'uploads' }>({ tab: 'uploads' })

function getMcoToken(): string | null {
  return mco.token || (typeof localStorage !== 'undefined' && localStorage.getItem('mco_access_token')) || null
}

/**
 * Rename an ingest job's topic (the user-visible document name).
 * The chunks in claude_memory keep their "(Part N/M)" stems — we don't
 * rewrite 1793 rows per rename. The UI's DOCUMENT group-by strips the
 * Part-suffix when grouping, so the bare topic from the job row is what
 * appears as the group header.
 */
export async function renameIngestJob(jobId: string, newTopic: string): Promise<void> {
  const token = getMcoToken()
  if (!token) throw new Error('not signed in to MCO')
  ingestQueue.mutating.add(jobId)
  try {
    const r = await fetch(`${NR_BASE}/ingest/jobs/${jobId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ topic: newTopic }),
    })
    const j = await r.json().catch(() => ({}))
    if (!r.ok || !j.ok) {
      throw new Error(j.error || `rename failed (HTTP ${r.status})`)
    }
    // Patch in-place so the UI updates immediately without a refetch round-trip.
    const idx = ingestQueue.jobs.findIndex((row) => row.job_id === jobId)
    if (idx >= 0) ingestQueue.jobs[idx] = { ...ingestQueue.jobs[idx], topic: j.job.topic }
  } finally {
    ingestQueue.mutating.delete(jobId)
  }
}

/**
 * Delete an ingest job + all its claude_memory chunks (+ their relationships
 * via FK CASCADE). Server-side fires pg_notify so mv_memory_layout refreshes
 * within ~5s and the chunks vanish from the constellation.
 */
export async function deleteIngestJob(jobId: string): Promise<void> {
  const token = getMcoToken()
  if (!token) throw new Error('not signed in to MCO')
  ingestQueue.mutating.add(jobId)
  try {
    const r = await fetch(`${NR_BASE}/ingest/jobs/${jobId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    const j = await r.json().catch(() => ({}))
    if (!r.ok || !j.ok) {
      throw new Error(j.error || `delete failed (HTTP ${r.status})`)
    }
    // Drop from local state immediately.
    ingestQueue.jobs = ingestQueue.jobs.filter((row) => row.job_id !== jobId)
  } finally {
    ingestQueue.mutating.delete(jobId)
  }
}

let _ingestInFlight: AbortController | null = null
export async function loadIngestQueue(): Promise<void> {
  const token = mco.token || (typeof localStorage !== 'undefined' && localStorage.getItem('mco_access_token')) || null
  if (!token) {
    ingestQueue.error = 'not signed in to MCO'
    ingestQueue.jobs = []
    return
  }
  // Cancel any in-flight request to avoid out-of-order updates
  if (_ingestInFlight) _ingestInFlight.abort()
  _ingestInFlight = new AbortController()
  ingestQueue.loading = true
  try {
    const r = await fetch(`${NR_BASE}/ingest/jobs?limit=50`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: _ingestInFlight.signal,
    })
    const j = await r.json().catch(() => ({}))
    if (!r.ok || !j.ok) {
      throw new Error(j.error || `ingest/jobs failed (HTTP ${r.status})`)
    }
    ingestQueue.jobs = Array.isArray(j.jobs) ? j.jobs as IngestJob[] : []
    ingestQueue.error = null
    ingestQueue.lastFetchedAt = Date.now()
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') return
    ingestQueue.error = (err as Error).message
  } finally {
    ingestQueue.loading = false
    _ingestInFlight = null
  }
}

/**
 * UI overrides: per-memory display state set by the user via the memory
 * list panel. Constellation reads these in pointColor/pointRadius and
 * filters visible points by `hidden`.
 *
 * SvelteSet / SvelteMap: reactive collection types — mutations (.add /
 * .delete / .set) trigger downstream $derived/$effect, unlike plain
 * Set/Map which need re-assignment to fire reactivity.
 */
export const overrides = $state<{
  hidden: SvelteSet<number>
  highlighted: SvelteSet<number>
  /** [r,g,b] in 0-255. Alpha is determined by selection state. */
  customColors: SvelteMap<number, [number, number, number]>
}>({
  hidden: new SvelteSet<number>(),
  highlighted: new SvelteSet<number>(),
  customColors: new SvelteMap<number, [number, number, number]>(),
})

export function toggleHidden(id: number): void {
  if (overrides.hidden.has(id)) overrides.hidden.delete(id)
  else overrides.hidden.add(id)
}
export function toggleHighlighted(id: number): void {
  if (overrides.highlighted.has(id)) overrides.highlighted.delete(id)
  else overrides.highlighted.add(id)
}
export function setCustomColor(id: number, color: [number, number, number] | null): void {
  if (color === null) overrides.customColors.delete(id)
  else overrides.customColors.set(id, color)
}
export function clearAllOverrides(): void {
  overrides.hidden.clear()
  overrides.highlighted.clear()
  overrides.customColors.clear()
}

/** UI lock for the left flyout drawer. */
export const drawer = $state<{ open: boolean; tab: 'filters' | 'memories' | 'files' | 'settings' }>({
  open: false,
  tab: 'filters',
})

/**
 * Walk trail — sequence of visited memories. Every selectedId change
 * pushes a step (deduped if same id as last). The Constellation renders
 * the trail as a polyline; the detail panel shows clickable breadcrumbs.
 */
export interface TrailStep {
  id: number
  ts: number
  via: 'click' | 'follow' | 'manual'
}
export const trail = $state<{ steps: TrailStep[] }>({ steps: [] })

const TRAIL_MAX = 50

export function pushTrailStep(id: number, via: TrailStep['via']): void {
  const last = trail.steps[trail.steps.length - 1]
  if (last && last.id === id) return  /* dedupe: don't repeat same id */
  trail.steps.push({ id, ts: Date.now(), via })
  if (trail.steps.length > TRAIL_MAX) trail.steps.shift()
}
export function clearTrail(): void {
  trail.steps = []
}
export function undoTrail(): void {
  trail.steps.pop()
  /* Re-select the previous step so the camera flies back. */
  const prev = trail.steps[trail.steps.length - 1]
  if (prev) selection.selectedId = prev.id
}

/**
 * Apply an override action to a list of memory ids in one batch. Used by
 * the group view's bulk action buttons (highlight all, hide all, color all).
 */
export function bulkHighlight(ids: number[], on: boolean): void {
  for (const id of ids) {
    if (on) overrides.highlighted.add(id)
    else overrides.highlighted.delete(id)
  }
}
export function bulkHide(ids: number[], on: boolean): void {
  for (const id of ids) {
    if (on) overrides.hidden.add(id)
    else overrides.hidden.delete(id)
  }
}
export function bulkColor(ids: number[], color: [number, number, number] | null): void {
  for (const id of ids) {
    if (color === null) overrides.customColors.delete(id)
    else overrides.customColors.set(id, color)
  }
}

/* ── Filter helpers ─────────────────────────────────────────────── */

export function tierThresholds(tier: ImportanceTier): { min: number; max: number } {
  switch (tier) {
    case 'all':      return { min: -10, max: 25 }
    case 'baseline': return { min: 0,   max: 4  }
    case 'high':     return { min: 5,   max: 14 }
    case 'elite':    return { min: 15,  max: 19 }
    case 'apex':     return { min: 20,  max: 25 }
  }
}

/** Pure predicate driving client-side filtering on top of server results. */
export function passesFilter(p: LayoutPoint, f: FilterState): boolean {
  const { min, max } = tierThresholds(f.tier)
  const i = p.importance ?? 0
  if (i < min || i > max) return false
  if (f.search) {
    const q = f.search.toLowerCase()
    const t = (p.topic ?? '').toLowerCase()
    if (!t.includes(q)) return false
  }
  return true
}
